import React, {
  createContext, FC, PropsWithChildren, useCallback, useContext, useMemo, useState,
} from 'react';
import { getListingLocationSlug, getReturnTime, timeStringToDate } from 'src/common/helpers';
import useSavedInquiry from 'src/common/hooks/useSavedInquiry';
import submitInquiry from 'src/inquiry/utils/submitInquiry';
import { inquirySubmitTracker } from 'src/inquiry/utils/sendInquiryForGuestUser';
import { getInquirySuccessUrl } from 'src/listing/utils/getBookingUrls';
import { useRouter } from 'next/router';
import { InquiryData } from 'src/inquiry/types';
import { useUserContext } from 'src/common/providers/UserProvider';
import broadcastNotification from 'src/common/components/Notification/broadcastNotification';
import { trackEvent } from 'src/common/3rdParty/tracking';
import { TripCartFormState } from 'src/listing/types';
import { Form } from 'react-final-form';
import { useTripCartContext } from 'src/trip-cart-deprecated/hooks/useTripCartContext';
import TripCartForm from 'src/trip-cart-deprecated/components/TripCartForm';
import { v4 as uuidv4 } from 'uuid';
import { sessionStore } from 'src/common/utils/storageWrapper';
import { EXTERNAL_PATHS, PATHS } from 'src/common/constants';
import { useProceedToCheckoutStep } from 'src/checkout/hooks/useProceedToCheckoutStep';
import { determineCheckoutStep } from 'src/checkout/utils/determineCheckoutStep';
import useFeatureFlag from 'src/common/hooks/useFeatureFlag';
import { trackTripCartBookNowProceedToPaymentClick } from 'src/trip-cart-deprecated/utils/trackingTripCart';
import checkForContactInfo from 'src/common/utils/checkForContactInfo';
import captureException from 'src/common/utils/captureException';
import { InstabookAvailability } from 'src/common/types/api/v4/instabook';
import { getDurationHours } from 'src/listing/helper';
import { BOOK_NOW_PICK_UP_TIME_FORMAT, formatDate } from 'src/common/utils/dateHelpers';
import { FORM_ERROR, FormApi, SubmissionErrors } from 'final-form';
import { compress } from 'lz-string';
import ensureString from 'src/common/utils/ensureString';
import getBlockedTimesForDate from 'src/trip-cart-deprecated/utils/getBlockedTimesForDate';
import { validateDepartureTime } from 'src/trip-cart-deprecated/hooks/useTripCartOptionsContext/getTripCartOptions';
import { clearCaches } from 'src/trip-cart-deprecated/utils/tripCartCache';
import { useTranslation } from 'next-i18next';
import useBookingMotivatorsContext from 'src/common/providers/useBookingMotivators';
import { getInstabookAvailabilitiesOnDate } from 'src/trip-cart-deprecated/utils/tripCartUtils';
import { useCurrencyContext } from 'src/common/providers/CurrencyProvider';
import getInstabookDepartureTimes from 'src/listing/utils/getInstabookDepartureTimes';
import useDateFnsLocale from 'src/common/hooks/useDateFnsLocale';
import { mapFormStateToInquiryData } from 'src/trip-cart-deprecated/helper';
import calculatePrices, { PriceParams } from 'src/trip-cart-deprecated/utils/calculatePrices';
import { TripCartPrice } from 'src/common/types';
import useTripCartPersistence from './useTripCartPersistence';

type TripCartBookingContextProps = {
  isSubmitting: boolean;
  contentWarningVisible: boolean;
  setTierPricing: (values: TierPricingHistory | undefined) => void;
};

const TripCartBookingContext = createContext<TripCartBookingContextProps | null>(null);

type TierPricingHistory = {
  params: PriceParams;
  prices: TripCartPrice;
};
/**
 * Form submission for Inquiries and Instabooks (Book Now)
 */
const TripCartBookingProvider: FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter();
  const {
    listing, initialValues, selectedInstabookAvailability, loadInstabookTrips, showAiSummary,
  } = useTripCartContext();
  const { saveFormToStorage } = useTripCartPersistence();
  const { currency } = useCurrencyContext();
  const { user } = useUserContext();
  const isUserLoggedIn = !!user?.id;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const listingLocation = getListingLocationSlug(listing.breadcrumbs);
  const insuranceEnabled = useFeatureFlag('insurance');
  const [contentWarningVisible, setContentWarningVisible] = useState(false);
  const locale = useDateFnsLocale();
  const { loadBookingMotivators } = useBookingMotivatorsContext();
  const [t] = useTranslation('listing-page');
  const [tierPricing, setTierPricing] = useState<TierPricingHistory>();

  const showFormError = useCallback((key: string = 'trip-cart--form-error--general') => {
    broadcastNotification({
      i18nKey: 'trip-cart--form-error--general',
      translation: 'listing-page',
      variant: 'error',
    });

    return {
      [FORM_ERROR]: key,
    };
  }, []);

  // -------
  // Inquiry
  // -------

  const {
    storeInquiryInCookies,
    storeInquiryInSessionStorage,
    inquirySearchReferer,
  } = useSavedInquiry();

  const proceedWithInquiry = useCallback(async (inquiryData: InquiryData) => {
    setIsSubmitting(true);
    const inquiryResponse = await submitInquiry(inquiryData, listing.id);
    if (inquiryResponse?.id) {
      // handle inquiry submission success
      storeInquiryInSessionStorage(inquiryData);
      inquirySearchReferer.set();
      storeInquiryInCookies(inquiryData);
      inquirySubmitTracker(
        inquiryData,
        inquiryResponse.thread_id,
        listingLocation,
        showAiSummary,
      );

      // redirect to inquiry success page
      await router.push(getInquirySuccessUrl(listing.id, inquiryResponse.id.toString()));
      return true;
    }
    // handle inquiry submission failure
    setIsSubmitting(false);
    trackEvent('inquiry_submit_fail', {
      p1: 'server-error',
      p2: showAiSummary ? 'ai_summary_visible' : 'ai_summary_hidden',
      ...(listingLocation ? { listing_location: listingLocation } : {}),
    });
    return showFormError();
  }, [
    listing.id,
    listingLocation,
    router,
    storeInquiryInCookies,
    storeInquiryInSessionStorage,
    inquirySearchReferer,
    showFormError,
    showAiSummary,
  ]);

  const isAuthMigrationEnabled = useFeatureFlag('auth-migration');

  const proceedWithGuestUserInquiry = useCallback((inquiryData: InquiryData) => {
    try {
      /**
       * generate a unique key for the session storage and
       * store the inquiry data in it
       */
      setIsSubmitting(true);
      const key = uuidv4();

      // compress the comment field to reduce the size of the data
      const data = {
        ...inquiryData,
        comment: compress(ensureString(inquiryData.comment)),
      };
      sessionStore.setItem(key, JSON.stringify(data));

      inquirySearchReferer.set();

      const params = new URLSearchParams({
        next: `${PATHS.LISTING}${listing.id}${PATHS.INQUIRY_SUBMIT}?sessionKey=${key}`,
        return: `${PATHS.LISTING}${listing.id}/?sessionKey=${key}&sheet`,
      });

      if (isAuthMigrationEnabled) {
        const registerUrl = `${PATHS.REGISTER}?${params.toString()}`;
        window.location.href = registerUrl;
      } else {
        const contactUrl = `${EXTERNAL_PATHS.INQUIRY}${listing.id}/contact/?${params.toString()}`;
        window.location.href = contactUrl;
      }

      return true;
    } catch (error) {
      setIsSubmitting(false);
      captureException(`Failed to read sessionStorage: ${error}`);
      return showFormError();
    }
  }, [inquirySearchReferer, listing.id, showFormError, isAuthMigrationEnabled]);

  const initiateInquiryProcess = useCallback((values: TripCartFormState) => {
    if (!values.departureTime) {
      // Errors handled in submit
      return false;
    }
    const inquiryData = mapFormStateToInquiryData(values);
    if (isUserLoggedIn) {
      // submit inquiry for logged in user.
      return proceedWithInquiry(inquiryData);
    }
    // handle logged out user inquiry
    return proceedWithGuestUserInquiry(inquiryData);
  }, [isUserLoggedIn, proceedWithGuestUserInquiry, proceedWithInquiry]);

  // --------------------
  // Book Now / Instabook
  // --------------------
  const proceedToCheckoutStep = useProceedToCheckoutStep();

  const submitBookNow = useCallback(async (
    values: TripCartFormState,
    instabookAvailability: InstabookAvailability,
  ): Promise<SubmissionErrors | boolean> => {
    if (!values.departureTime) {
      // Errors handled in submit
      return false;
    }
    setIsSubmitting(true);

    const { success } = await proceedToCheckoutStep({
      checkoutType: 'trip-cart--instabook',
      formData: {
        departureTime: formatDate(
          timeStringToDate(values.departureTime),
          BOOK_NOW_PICK_UP_TIME_FORMAT,
        ),
        returnTime: getReturnTime(values.departureTime, getDurationHours(values.formDuration)),
        guests: {
          adults: values.formGuests,
        },
        captained: values.withCaptain,
        ownerMessage: values.message,
      },
      // Only called when this is available
      availabilityId: instabookAvailability.id,
      step: determineCheckoutStep({
        currentPage: 'book-now',
        featureFlags: { insuranceEnabled },
        userIsLoggedIn: !!user?.id,
      }),
      trackingEvent: trackTripCartBookNowProceedToPaymentClick,
    });
    setIsSubmitting(false);
    if (success) {
      return true;
    }

    return showFormError();
  }, [proceedToCheckoutStep, insuranceEnabled, user?.id, showFormError]);

  // -------
  // General
  // -------

  const validateBlockedTimesForSubmit = useCallback(async (
    values: TripCartFormState,
  ): Promise<SubmissionErrors | undefined> => {
    // ignore cache
    const blockedTimes = await getBlockedTimesForDate(listing.id, values.formSelectedDates[0]);
    const error = validateDepartureTime({
      value: values.departureTime!,
      durationHours: getDurationHours(values.formDuration),
      blockedTimes,
      date: values.formSelectedDates[0],
      listingTimeZone: listing.timezone,
      t,
    });
    if (error === undefined) {
      return undefined;
    }
    return showFormError('trip-cart--form-error--submit-blocked');
  }, [listing.id, listing.timezone, showFormError, t]);

  const validateInstabookAvailabilities = useCallback(async (
    values: TripCartFormState,
  ): Promise<SubmissionErrors | undefined> => {
    const { availabilities, durations } = await getInstabookAvailabilitiesOnDate(
      listing.id,
      values.formSelectedDates[0],
      values.formGuests,
      currency.code,
      // ignore cache
      true,
    ) ?? {};
    const match = availabilities?.find((a) => a.id === selectedInstabookAvailability?.id);
    if (!availabilities?.length || !durations?.length || !match) {
      return showFormError('trip-cart--form-error--submit-instabook');
    }
    const departureTimes = getInstabookDepartureTimes(match, locale);
    if (!departureTimes.some((time) => time.formattedTime24 === values.departureTime)) {
      return showFormError('trip-cart--form-error--submit-instabook');
    }
    // success
    return undefined;
  }, [currency.code, listing.id, locale, selectedInstabookAvailability?.id, showFormError]);

  const checkPricingMatches = useCallback(async (): Promise<boolean> => {
    if (!tierPricing?.params || !tierPricing?.prices) {
      return true;
    }
    const priceData = await calculatePrices(listing.id, { ...tierPricing.params });
    if (!priceData || !priceData.price_sections) {
      // Don't block the booking
      return true;
    }

    // Server error, duration/date is not available or doesn't exist
    if (!priceData.price_sections.length) {
      return false;
    }

    return (priceData.renter_booking_amount === tierPricing.prices.renter_booking_amount);
  }, [listing.id, tierPricing]);

  const submit = useCallback(async (
    values: TripCartFormState,
    form: FormApi<TripCartFormState, Partial<TripCartFormState>>,
  ): Promise<SubmissionErrors | boolean> => {
    if (!values.departureTime) {
      // track validation error for inquiry
      if (values.bookingMode === 'inquiry') {
        trackEvent('inquiry_submit_fail', {
          p1: 'validation-error',
          ...(listingLocation ? { listing_location: listingLocation } : {}),
        });
      }

      return {
        departureTime: 'trip-cart--form-error--departure-time',
      };
    }
    const { errors } = form.getState();
    if (Object.keys(errors ?? {}).length) {
      // Shown per field
      return false;
    }

    if (!contentWarningVisible && values.message) {
      try {
        const hasContactInfo = await checkForContactInfo([values.message]);
        if (hasContactInfo) {
          // 'content' errors will get auto-fixed by the backend, content window will run submit
          setContentWarningVisible(true);
          return true;
        }
      } catch (error) {
        captureException(error);
        return showFormError();
      }
    }

    // In case user reloads after error
    saveFormToStorage(values);

    const isPricingMatched = await checkPricingMatches();
    if (!isPricingMatched) {
      return showFormError('trip-cart--form-error--submit-price-mismatch');
    }

    const blockedError = await validateBlockedTimesForSubmit(values);
    if (blockedError) {
      // cache is no longer valid, clear all
      clearCaches();
      loadBookingMotivators(formatDate(values.formSelectedDates[0]), true);
      return blockedError;
    }

    if (values.bookingMode === 'instabook' && selectedInstabookAvailability) {
      const availabilitiesError = await validateInstabookAvailabilities(values);
      if (availabilitiesError) {
        // will use cache from above error check
        await loadInstabookTrips(values);
        // now clear them since other cache is invalid
        clearCaches();
        return availabilitiesError;
      }

      return submitBookNow(values, selectedInstabookAvailability);
    }

    return initiateInquiryProcess(values);
  }, [
    checkPricingMatches,
    validateBlockedTimesForSubmit,
    validateInstabookAvailabilities,
    loadInstabookTrips,
    loadBookingMotivators,
    contentWarningVisible,
    saveFormToStorage,
    selectedInstabookAvailability,
    showFormError,
    submitBookNow,
    listingLocation,
    initiateInquiryProcess,
  ]);

  const value = useMemo((): TripCartBookingContextProps => ({
    isSubmitting,
    contentWarningVisible,
    setTierPricing,
  }), [isSubmitting, contentWarningVisible, setTierPricing]);

  return (
    <TripCartBookingContext.Provider value={value}>
      <Form<TripCartFormState>
        keepDirtyOnReinitialize
        initialValues={initialValues}
        validateOnBlur
        onSubmit={submit}
      >
        {() => (
          <TripCartForm>
            {children}
          </TripCartForm>
        )}
      </Form>
    </TripCartBookingContext.Provider>
  );
};

export default TripCartBookingProvider;

export const useTripCartBookingContext = (): TripCartBookingContextProps => {
  const context = useContext(TripCartBookingContext);
  if (context == null) {
    throw new Error('useTripCartBookingContext must be used within a TripCartBookingProvider');
  }

  return context;
};
